import { useState } from "react";
import {
  Grid,
  TextField,
  Button,
  FormHelperText,
  CssBaseline,
  Typography,
  InputAdornment,
} from "@mui/material";
import Box from "@mui/material/Box";
import { Formik } from "formik";
import * as Yup from "yup";
import { useLocation, useHistory, NavLink, Link } from "react-router-dom";
import CalebAndBrownLogo from "src/icons/cnbDesktop";
import { LoginStyles } from "../auth/styles/Login.styles";
import { useIsMobile } from "src/utils/hooks/useIsMobile";
import CnbLogoMobile from "src/icons/CnbLogoMobile";
import api from "src/store/features/api";

export default function PasswordReset() {
  const search = useLocation().search;
  const oobCode = new URLSearchParams(search).get("code");
  const history = useHistory();
  const classes = LoginStyles();
  const isMobile = useIsMobile();

  const [showFirstPassword, setShowFirstPassword] = useState(false);
  const [showSecondPassword, setShowSecondPassword] = useState(false);
  const [handlePasswordReset] = api.useHandleResetPasswordMutation();

  // Can be used eventually
  // const continueUrl = new URLSearchParams(search).get("continueUrl");
  // const lang = new URLSearchParams(search).get("lang");

  const validationSchema = Yup.object().shape({
    password: Yup.string()
      .required("Please enter your password")
      .min(8, "Must contain 8 characters or more")
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[A-Z])/,
        "Must contain at least one uppercase",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[a-z])/,
        "Must contain at least one lowercase",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[0-9])/,
        "Must contain at least one number",
      )
      .matches(
        // eslint-disable-next-line no-useless-escape
        /^(?=.*[$&+,:;=?@#|'<>.^*()%!-\$%\^&\*\-])/,
        "Must contain at least one special character.",
      ),
    newPassword: Yup.string()
      .required("Password cannot be empty")
      .oneOf([Yup.ref("password"), null], "Passwords must match"),
  });

  const RedirectLink = (props: { message: string }) => {
    const { message } = props;
    const textPartOne = message.substring(0, message.indexOf("here"));
    const textPartTwo = message.substring(textPartOne.length + 5, message.length);
    return (
      <>
        <span>{textPartOne}</span>
        <NavLink
          to="reset/request"
          style={{
            textDecoration: "none",
          }}>
          <span style={{ color: "#fc4c4c", fontWeight: "bold", fontSize: "16px" }}>{" here "}</span>
        </NavLink>
        <span>{textPartTwo}</span>
      </>
    );
  };

  return (
    <div className={classes.container}>
      <CssBaseline />
      <Grid container className={classes.logoBox}>
        {isMobile ? <CnbLogoMobile height={"200px"} /> : <CalebAndBrownLogo />}
      </Grid>

      <Grid item xs={12}>
        <Formik
          initialValues={{
            password: "",
            newPassword: "",
            submit: null,
          }}
          onSubmit={async (values, { setErrors, setStatus, setSubmitting }): Promise<void> => {
            try {
              const { password, newPassword } = values;

              if (password === newPassword) {
                if (oobCode) {
                  await handlePasswordReset({
                    resetPasswordDto: { oobCode, newPassword },
                  });
                  // :TODO Handle password reset message
                  history.push("/");
                  console.info(`Password was successfully updated`);
                } else {
                  console.error(`Reset code missing`);
                  // Password matches but query params are missing
                  setErrors({ submit: "Reset code missing" });
                  setSubmitting(false);
                  setStatus({ success: false });
                }
              } else {
                // Password does not match
                console.error(`Password Reset: Password does not match `);
                setErrors({ submit: "Password does not match" });
                setSubmitting(false);
                setStatus({ success: false });
              }
            } catch (err: any) {
              // Something went wrong during user password reset
              console.error(`Password Reset Error: `);
              if (err.message.includes("expired")) {
                setErrors({ submit: err.message });
              } else {
                if (err.status === 403) {
                  console.error(`Expired link`);
                  setErrors({ submit: "Expired link" });
                } else {
                  setErrors({ submit: "Something went wrong" });
                }
              }
              setSubmitting(false);
              setStatus({ success: false });
            }
          }}
          validationSchema={validationSchema}>
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            isSubmitting,
            touched,
            values,
          }): JSX.Element => (
            <form noValidate onSubmit={handleSubmit} className={classes.formStyles}>
              <TextField
                InputProps={{
                  disableUnderline: true,
                  className: classes.heightTextField,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        style={{
                          color: "#fff",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() => setShowFirstPassword(!showFirstPassword)}>
                        {showFirstPassword ? "Hide" : "Show"}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  className: classes.label,
                }}
                className={classes.root}
                error={Boolean(touched.password && errors.password)}
                fullWidth
                helperText={touched.password && errors.password}
                label="Password"
                margin="normal"
                name="password"
                onBlur={handleBlur}
                onChange={handleChange}
                type={showFirstPassword ? "text" : "password"}
                value={values.password}
                variant="filled"
                autoComplete="on"
              />
              <TextField
                InputProps={{
                  disableUnderline: true,
                  className: classes.heightTextField,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Typography
                        style={{
                          color: "#fff",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                        onClick={() => setShowSecondPassword(!showSecondPassword)}>
                        {showSecondPassword ? "Hide" : "Show"}
                      </Typography>
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  className: classes.label,
                }}
                className={classes.root}
                error={Boolean(touched.newPassword && errors.newPassword)}
                fullWidth
                helperText={touched.newPassword && errors.newPassword}
                label="Confirm Password"
                margin="normal"
                name="newPassword"
                onBlur={handleBlur}
                onChange={handleChange}
                type={showSecondPassword ? "text" : "password"}
                value={values.newPassword}
                variant="filled"
                autoComplete="on"
              />
              {errors.submit && (
                <Box sx={{ mt: 2 }}>
                  <FormHelperText className={classes.errorMessage}>
                    {errors.submit.includes("expired") ? (
                      <RedirectLink message={errors.submit} />
                    ) : (
                      <>{errors.submit}</>
                    )}
                  </FormHelperText>
                </Box>
              )}
              <Box sx={{ mt: 2 }}>
                <Button
                  disableRipple
                  className={classes.button}
                  color="secondary"
                  disabled={isSubmitting}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  Confirm password
                </Button>
              </Box>
              <Grid item xs={12}>
                <Typography
                  style={{
                    textAlign: "center",
                    paddingLeft: "25px",
                    paddingRight: "25px",
                    paddingTop: "20px",
                    marginBottom: "20px",
                    fontSize: "14px",
                  }}>
                  Already have an account?{" "}
                  <Link to="/" className={classes.loginLinkStyle}>
                    Login
                  </Link>
                </Typography>
              </Grid>
            </form>
          )}
        </Formik>
      </Grid>
    </div>
  );
}
